/**
 * Buy Now Button Handler for India Domain (Razorpay Magic Checkout)
 * 
 * Handles Buy Now button clicks on product pages:
 * 1. Adds product to cart
 * 2. Immediately initializes Razorpay Magic Checkout with updated cart
 * 
 * FAST IMPLEMENTATION: Direct API call, no delays, no button clicks
 */

(function() {
  'use strict';

  // Check if this is India domain (not international market)
  function isIndiaDomain() {
    if (window.MarketConfig && typeof window.MarketConfig.isInternationalMarket === 'function') {
      return !window.MarketConfig.isInternationalMarket();
    }
    
    if (window.WebhookHelper && typeof window.WebhookHelper.isInternationalMarket === 'function') {
      return !window.WebhookHelper.isInternationalMarket();
    }
    
    const path = window.location.pathname.toLowerCase();
    // If path doesn't contain international market paths, it's India
    return !/^\/(en-(?:ae|sa|gb|sg|us|eu|ca|au|my))(?:\/|$)/.test(path) &&
           !/\/en-(?:ae|sa|gb|sg|us|eu|ca|au|my)(?:\/|$)/.test(path);
  }

  // Only run on India domain
  if (!isIndiaDomain()) {
    return;
  }

  // Get cart data
  function getCartData() {
    return fetch('/cart.js')
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch cart');
        return res.json();
      })
      .catch(() => null);
  }

  // Add product to cart
  async function addProductToCart(form) {
    const formData = new FormData(form);
    
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/json',
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to add to cart' }));
        throw new Error(errorData.message || errorData.description || 'Failed to add product to cart');
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('[Buy Now India] Error adding to cart:', error);
      throw error;
    }
  }

  // Initialize Razorpay Magic Checkout directly
  function initializeRazorpayMagicCheckout() {
    // Method 1: Try direct API call (FASTEST)
    if (typeof window.openRzpCheckout === 'function') {
      console.log('[Buy Now India] Initializing Razorpay Magic Checkout via direct API...');
      try {
        window.openRzpCheckout();
        return true;
      } catch (error) {
        console.error('[Buy Now India] Direct API call failed:', error);
      }
    }
    
    // Method 2: Try corner.overrideCheckout if available
    if (typeof corner !== 'undefined' && typeof corner.overrideCheckout === 'function') {
      console.log('[Buy Now India] Initializing Razorpay Magic Checkout via corner.overrideCheckout...');
      try {
        corner.overrideCheckout((cartObject, discountCodeApplied) => {
          if (typeof window.openRzpCheckout === 'function') {
            window.openRzpCheckout();
          }
        });
        return true;
      } catch (error) {
        console.error('[Buy Now India] corner.overrideCheckout failed:', error);
      }
    }
    
    // Method 3: Fallback - find and click checkout button
    console.log('[Buy Now India] Falling back to checkout button click...');
    const checkoutButton = document.querySelector('.cart-drawer button[name="checkout"]') ||
                          document.querySelector('.cart-drawer .cart__checkout') ||
                          document.querySelector('[data-cart-checkout-button] button[name="checkout"]') ||
                          document.querySelector('.cart button[name="checkout"]') ||
                          document.querySelector('.cart .cart__checkout');
    
    if (checkoutButton) {
      checkoutButton.click();
      return true;
    }
    
    console.error('[Buy Now India] No Razorpay Magic Checkout method available');
    return false;
  }

  // Show loading state
  function showButtonLoading(button) {
    button.disabled = true;
    button.dataset.processing = 'true';
    button.classList.add('is-loading');
    
    const textWrapper = button.querySelector('.btn__text');
    const loader = button.querySelector('.btn__loader');
    
    if (textWrapper) {
      textWrapper.style.opacity = '0.5';
    }
    
    if (loader) {
      loader.style.opacity = '1';
      loader.style.display = 'block';
    }
  }

  // Hide loading state
  function hideButtonLoading(button) {
    button.disabled = false;
    button.dataset.processing = 'false';
    button.classList.remove('is-loading');
    
    const textWrapper = button.querySelector('.btn__text');
    const loader = button.querySelector('.btn__loader');
    
    if (textWrapper) {
      textWrapper.style.opacity = '1';
    }
    
    if (loader) {
      loader.style.opacity = '0';
      loader.style.display = 'none';
    }
  }

  // Main handler function
  async function handleBuyNowIndia(event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const button = event.currentTarget || event.target || event.target.closest('[data-buy-now-button-india]');
    
    if (!button) {
      console.warn('[Buy Now India] No button found');
      return;
    }

    // Prevent multiple clicks
    if (button.disabled || button.dataset.processing === 'true') {
      return;
    }

    showButtonLoading(button);

    try {
      // Get product form
      const form = button.closest('form[data-product-form]') || button.closest('form.product-form');
      if (!form) {
        throw new Error('Product form not found');
      }

      // Extract variant ID from form
      let variantId = null;
      const formId = form.id;
      
      // Method 1: Input inside form
      const variantInputInForm = form.querySelector('input[name="id"]');
      if (variantInputInForm && variantInputInForm.value) {
        variantId = variantInputInForm.value;
      }
      
      // Method 2: Input with form attribute
      if (!variantId && formId) {
        const variantInputWithFormAttr = document.querySelector(`input[name="id"][form="${formId}"]`);
        if (variantInputWithFormAttr && variantInputWithFormAttr.value) {
          variantId = variantInputWithFormAttr.value;
        }
      }
      
      // Method 3: Form wrapper
      if (!variantId) {
        const formWrapper = form.closest('[data-form-wrapper]');
        if (formWrapper) {
          const variantInputInWrapper = formWrapper.querySelector('input[name="id"]');
          if (variantInputInWrapper && variantInputInWrapper.value) {
            variantId = variantInputInWrapper.value;
          }
        }
      }
      
      // Method 4: Fallback to FormData
      if (!variantId) {
        const formData = new FormData(form);
        variantId = formData.get('id') || formData.get('variant_id');
      }
      
      if (!variantId) {
        console.error('[Buy Now India] ❌ Variant ID not found');
        throw new Error('Variant ID not found in form. Please select a variant and try again.');
      }

      // Get quantity
      let quantity = 1;
      const quantityInputInForm = form.querySelector('input[name="quantity"]');
      if (quantityInputInForm && quantityInputInForm.value) {
        quantity = parseInt(quantityInputInForm.value, 10) || 1;
      } else if (formId) {
        const quantityInputWithFormAttr = document.querySelector(`input[name="quantity"][form="${formId}"]`);
        if (quantityInputWithFormAttr && quantityInputWithFormAttr.value) {
          quantity = parseInt(quantityInputWithFormAttr.value, 10) || 1;
        }
      }

      console.log('[Buy Now India] Adding product to cart...', { variantId, quantity });

      // Add product to cart. If gold plating is selected, bundle the plating
      // line item using the shared helper (same path as international Buy Now);
      // otherwise fall back to the single-item add. Magic Checkout then reads
      // the live cart, which includes both lines.
      let addResult;
      const goldPlatingBody =
        window.GoldPlatingCart && window.GoldPlatingCart.buildMultiItemBody(form);
      if (goldPlatingBody) {
        console.log('[Buy Now India] Gold plating selected — bundling plating line item');
        addResult = await window.GoldPlatingCart.postJsonAdd(
          window.GoldPlatingCart.cartAddJsUrl(),
          goldPlatingBody
        );
      } else {
        addResult = await addProductToCart(form);
      }

      if (addResult.status) {
        // Error adding to cart
        hideButtonLoading(button);
        alert(addResult.description || 'Unable to add product to cart. Please try again.');
        return;
      }

      console.log('[Buy Now India] ✅ Product added to cart, initializing Razorpay Magic Checkout...');

      // Send InitiateCheckout webhook
      if (window.dispatchEvent) {
        window.dispatchEvent(new CustomEvent('buy-now-india:clicked', {
          bubbles: true,
          cancelable: true
        }));
      }

      // CRITICAL: Immediately initialize Razorpay Magic Checkout
      // Razorpay will read the cart from Shopify, which should be updated by now
      // No delays, no polling - just direct API call
      const initialized = initializeRazorpayMagicCheckout();
      
      if (!initialized) {
        throw new Error('Failed to initialize Razorpay Magic Checkout. Please try again.');
      }

      // Don't hide loading state - let Razorpay handle the UI
      // The checkout modal will open, so we keep the button in loading state

    } catch (error) {
      console.error('[Buy Now India] Error:', error);
      hideButtonLoading(button);
      
      // Show user-friendly error message
      const errorMessage = error.message || 'Unable to process Buy Now. Please try again.';
      alert(errorMessage);
    }
  }

  // Initialize Buy Now buttons
  function initBuyNowButtons() {
    const buyNowButtons = document.querySelectorAll('[data-buy-now-button-india]');
    
    buyNowButtons.forEach(button => {
      if (button.dataset.buyNowHandlerAttached) {
        return; // Already initialized
      }
      
      button.dataset.buyNowHandlerAttached = 'true';
      button.addEventListener('click', handleBuyNowIndia, true);
    });
  }

  // Set up event delegation
  function setupEventDelegation() {
    if (!document.body) {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', setupEventDelegation);
      } else {
        setTimeout(setupEventDelegation, 50);
      }
      return;
    }

    // Use capture phase for early interception
    document.body.addEventListener('click', function(e) {
      const button = e.target.closest('[data-buy-now-button-india]');
      if (button) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        handleBuyNowIndia({
          preventDefault: () => {},
          stopPropagation: () => {},
          stopImmediatePropagation: () => {},
          currentTarget: button,
          target: button
        });
        return false;
      }
    }, true);
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      setupEventDelegation();
      initBuyNowButtons();
      setTimeout(initBuyNowButtons, 200);
    });
  } else {
    setupEventDelegation();
    initBuyNowButtons();
    setTimeout(initBuyNowButtons, 200);
  }

  // Watch for dynamically added buttons
  if (typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver(function(mutations) {
      const hasNewButtons = mutations.some(mutation => 
        Array.from(mutation.addedNodes).some(node => 
          node.nodeType === 1 && (
            (node.hasAttribute && node.hasAttribute('data-buy-now-button-india')) ||
            (node.querySelector && node.querySelector('[data-buy-now-button-india]'))
          )
        )
      );
      
      if (hasNewButtons) {
        initBuyNowButtons();
      }
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // Additional delayed initialization
  setTimeout(initBuyNowButtons, 1000);
})();
